import { Injectable } from '@angular/core';
import { WebApiService } from './web-api.service';
import { ApiUrlConstants } from '../common/ApiUrlConstants';
import {
  RfqKycRequest,
  RfqKycRequestDocument,
} from './rfq-kyc-request.service';

// =========================================================================
// Response shape returned by the /rfq/kycToken/verify endpoint.
//
// The backend distinguishes INVALID_TOKEN (no row in kycTokenDetails)
// from EXPIRED_TOKEN (row exists but expires_at < NOW()). The page uses
// this discriminator to pick the right error copy — "your link is no
// longer valid" reads very differently from "your link has expired,
// please ask the buyer for a fresh invitation".
// =========================================================================
export interface KycTokenVerifyResponse {
  valid: boolean;
  reason?: 'INVALID_TOKEN' | 'EXPIRED_TOKEN' | 'ALREADY_USED' | null;
  kycRequestId?: number;
  buyerId?: number;
  supplierId?: number;
  // Display fields so the page can greet without a second round trip.
  supplierFirstName?: string;
  supplierLastName?: string;
  supplierCompanyName?: string;
  buyerFirstName?: string;
  buyerLastName?: string;
  buyerCompanyName?: string;
  expiresAt?: string;
}

/**
 * Wrapper around the four PUBLIC endpoints behind the magic-link
 * submission flow:
 *
 *     POST /rfq/kycToken/verify        → KycTokenVerifyResponse
 *     POST /rfq/kycToken/getByToken    → RfqKycRequest
 *     POST /rfq/kycToken/upload        → RfqKycRequestDocument
 *     POST /rfq/kycToken/submit        → RfqKycRequest
 *
 * Every call carries the plain short token in the request body. The
 * backend hashes the token with SHA-256, validates the matching row
 * in kycTokenDetails (existence + expiry), and only then performs the
 * requested action.
 *
 * NOTE: WebApiService strips the outer `{ data: { ... } }` envelope so
 * callers receive the inner payload directly.
 */
@Injectable({ providedIn: 'root' })
export class KycTokenSubmissionService {

  constructor(private webApi: WebApiService) {}

  // ---------------------------------------------------------------------
  // 1. Verify the token from the URL query param. Page-load step.
  //    Always 200s — the reason is in the response body so the page
  //    can pick the right error copy without try/catch noise.
  // ---------------------------------------------------------------------
  async verify(token: string): Promise<KycTokenVerifyResponse> {
    return this.webApi.postRequest<KycTokenVerifyResponse>(
      ApiUrlConstants.kycTokenVerify,
      { token }
    );
  }

  // ---------------------------------------------------------------------
  // 2. Fetch the full bundle (documents + buyer/supplier context).
  // ---------------------------------------------------------------------
  async getByToken(token: string): Promise<RfqKycRequest> {
    return this.webApi.postRequest<RfqKycRequest>(
      ApiUrlConstants.kycTokenGetByToken,
      { token }
    );
  }

  // ---------------------------------------------------------------------
  // 3. Persist one uploaded document slot.
  //    File bytes go directly to S3 via presigned PUT (handled by the
  //    page using AwsSdkService). This call only records metadata.
  // ---------------------------------------------------------------------
  async upload(payload: {
    token: string;
    documentId: number;
    fileUrl: string;
    fileName: string;
    fileSizeBytes: number;
    fileMimeType: string;
  }): Promise<RfqKycRequestDocument> {
    return this.webApi.postRequest<RfqKycRequestDocument>(
      ApiUrlConstants.kycTokenUpload,
      payload
    );
  }

  // ---------------------------------------------------------------------
  // 4. Finalise the bundle. Flips status to UPLOADED, stamps the
  //    token's used_at, fires the buyer notification email.
  // ---------------------------------------------------------------------
  async submit(token: string): Promise<RfqKycRequest> {
    return this.webApi.postRequest<RfqKycRequest>(
      ApiUrlConstants.kycTokenSubmit,
      { token }
    );
  }
}
